import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { EmptyState, LoadingState } from "../../../../components/UI/States";
import { Segmented } from "../../../../components/WindowDoor/wdUi";
import {
  STATUS,
  formatDate,
  statusStyle,
} from "../../../../components/WindowDoor/ServiceRequests/constants";
import {
  fetchServiceRequestActivity,
  fetchServiceRequestMessages,
  postServiceRequestMessage,
  serviceRequestFileUrl,
  uploadServiceRequestFiles,
} from "../../../../components/WindowDoor/ServiceRequests/api";

// The shop dashboard keeps its filters, search, sort and page in the URL.
// The last one is remembered for this tab so the detail page's "All
// requests" link goes back to the same view, and so does the scroll position
// the user left it at.
const DASHBOARD_PATH = "/windowdoor/service-requests";
const DASHBOARD_SEARCH_KEY = "serviceRequests.dashboardSearch";
const DASHBOARD_SCROLL_KEY = "serviceRequests.dashboardScroll";

const sessionGet = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const sessionSet = (key, value) => {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked (private mode): Back and refresh still work off the URL.
  }
};

export const rememberDashboardSearch = (search) =>
  sessionSet(DASHBOARD_SEARCH_KEY, search || "");

export const dashboardPath = () => {
  const search = sessionGet(DASHBOARD_SEARCH_KEY);
  return search ? `${DASHBOARD_PATH}?${search}` : DASHBOARD_PATH;
};

/** Saved on opening a request; read back (once) when the same view returns. */
export const rememberDashboardScroll = (search) =>
  sessionSet(DASHBOARD_SCROLL_KEY, JSON.stringify({ search: search || "", y: window.scrollY }));

export const takeDashboardScroll = (search) => {
  const raw = sessionGet(DASHBOARD_SCROLL_KEY);
  sessionSet(DASHBOARD_SCROLL_KEY, null);
  try {
    const saved = JSON.parse(raw);
    return saved && saved.search === (search || "") ? Number(saved.y) || 0 : 0;
  } catch {
    return 0;
  }
};

// ---------------------------------------------------------------------------
// Activity log — every change and every email (sent or failed), written by
// the server (logActivity in serviceRequests.js). Same view for everyone who
// can open the request; without a requestId it's the whole queue's log.
// ---------------------------------------------------------------------------

const ACTIVITY_ICONS = {
  created: AddCircleOutlineIcon,
  checklist: TaskAltIcon,
  status: FlagOutlinedIcon,
  assigned: PersonOutlineIcon,
  edited: EditOutlinedIcon,
  files: AttachFileIcon,
  message: ChatBubbleOutlineIcon,
  email: MailOutlineIcon,
  digest: ScheduleSendIcon,
};

const EMAIL_KINDS = ["email", "digest"];

const ACTIVITY_FILTERS = [
  { key: "all", label: "All", test: () => true },
  { key: "changes", label: "Changes", test: (entry) => !EMAIL_KINDS.includes(entry.kind) },
  { key: "emails", label: "Emails", test: (entry) => EMAIL_KINDS.includes(entry.kind) },
  { key: "failed", label: "Failed", test: (entry) => entry.ok === false },
];

// Matches ACTIVITY_LIMIT on the server.
const ACTIVITY_LIMIT = 300;

const ActivityEntry = ({ entry, showRequest, onOpenRequest }) => {
  const failed = entry.ok === false;
  const Icon = failed ? ErrorOutlineIcon : ACTIVITY_ICONS[entry.kind] || HistoryIcon;
  return (
    <li className="flex gap-3 border-b app-divider py-3 last:border-b-0">
      <span
        aria-hidden="true"
        className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          failed
            ? "bg-red-500/15 text-red-600 dark:text-red-400"
            : "bg-sky-500/10 text-sky-700 dark:text-sky-300"
        }`}
      >
        <Icon sx={{ fontSize: 18 }} />
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={`break-words text-sm ${
            failed ? "font-semibold text-red-700 dark:text-red-300" : ""
          }`}
        >
          {entry.summary}
        </p>
        {entry.error && (
          <p className="break-words text-xs text-red-600 dark:text-red-400">
            Error: {entry.error}
          </p>
        )}
        {entry.changes?.length > 0 && (
          <ul className="app-muted mt-1 space-y-0.5 text-xs">
            {entry.changes.map((change) => (
              <li key={change.label} className="break-words">
                {change.label}: “{change.from || "—"}” → “{change.to || "—"}”
              </li>
            ))}
          </ul>
        )}
        {entry.cc?.length > 0 && (
          <p className="app-muted break-words text-xs">CC: {entry.cc.join(", ")}</p>
        )}
        <p className="app-muted mt-0.5 text-xs">
          {showRequest && entry.request_id && (
            <>
              <button
                type="button"
                onClick={() => onOpenRequest(entry.request_id)}
                className="app-link font-semibold"
              >
                #{entry.entry_number}
              </button>
              {" · "}
            </>
          )}
          {formatDate(entry.at)} · {entry.by?.name || "Automatic"}
        </p>
      </div>
    </li>
  );
};

/**
 * "Activity log" button + popup. requestId: one request's log; omit for the
 * queue-wide log, where each entry links to its request via onOpenRequest(id).
 */
export const ActivityLogButton = ({ requestId, onOpenRequest }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const queue = !requestId;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setEntries(null);
    setError(null);
    fetchServiceRequestActivity(requestId)
      .then((data) => {
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setEntries([]);
        setError(err.message || "Could not load the activity log.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, requestId]);

  const close = () => setOpen(false);
  const test = ACTIVITY_FILTERS.find((option) => option.key === filter).test;
  const shown = (entries || []).filter(test);
  const failedCount = (entries || []).filter((entry) => entry.ok === false).length;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<HistoryIcon />}
        onClick={() => setOpen(true)}
      >
        Activity log
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle className="flex items-center justify-between gap-3 !pr-3">
          {queue ? "Service request activity" : "Activity log"}
          <IconButton onClick={close} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <p className="app-muted mb-3 text-sm">
            {queue
              ? "Every change and email across all service requests, including the 9 AM unassigned email, newest first."
              : "Every change to this request and every email about it — sent or failed — newest first."}
          </p>
          <Segmented
            ariaLabel="Filter the activity log"
            value={filter}
            onChange={setFilter}
            options={ACTIVITY_FILTERS.map(({ key, label }) => ({
              key,
              label,
              badge:
                key === "failed" && failedCount ? (
                  <span className="rounded-full bg-red-500/15 px-1.5 text-xs tabular-nums text-red-600 dark:text-red-400">
                    {failedCount}
                  </span>
                ) : null,
            }))}
          />
          {error && (
            <Alert severity="error" className="!mt-3">
              {error}
            </Alert>
          )}
          {entries === null ? (
            <LoadingState label="Loading activity…" />
          ) : shown.length ? (
            <ul className="mt-2">
              {shown.map((entry) => (
                <ActivityEntry
                  key={entry._id}
                  entry={entry}
                  showRequest={queue}
                  onOpenRequest={(id) => {
                    close();
                    onOpenRequest?.(id);
                  }}
                />
              ))}
            </ul>
          ) : (
            !error && (
              <EmptyState
                icon={HistoryIcon}
                title={filter === "failed" ? "Nothing failed" : "No activity yet"}
                description={
                  filter === "all"
                    ? "Changes and emails show up here as they happen."
                    : "Nothing here matches this filter."
                }
              />
            )
          )}
          {entries?.length >= ACTIVITY_LIMIT && (
            <p className="app-muted mt-3 text-xs">
              Showing the latest {ACTIVITY_LIMIT} entries.
            </p>
          )}
          {!queue && entries && !entries.some((entry) => entry.kind === "created") && (
            <p className="app-surface mt-3 rounded-lg px-3 py-2 text-xs app-muted">
              This request is older than the activity log, so changes made
              before the log started aren't listed. The chat still shows its
              earlier status changes and edits.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const StatusChip = ({ status }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      statusStyle(status).chip
    }`}
  >
    {status || "Submitted"}
  </span>
);

/** Label/value pair used all over the detail pages. */
export const DetailRow = ({ label, value, className = "" }) => (
  <div className={`min-w-0 ${className}`}>
    <p className="app-muted text-xs font-medium">{label}</p>
    <p className="mt-0.5 break-words text-sm font-semibold">{value || "—"}</p>
  </div>
);

/** Headline fact for the summary strip at the top of a detail page.
    `accent` (a border-l-* class) tints the small marker bar. */
export const SummaryCard = ({ label, value, accent, children }) => (
  <div className="app-card relative min-w-0 overflow-hidden rounded-2xl border p-4 pl-5">
    <span
      aria-hidden="true"
      className={`absolute inset-y-4 left-0 rounded-r-full border-l-[3px] ${
        accent || "border-l-sky-500"
      }`}
    />
    <p className="app-muted text-xs font-medium">{label}</p>
    {children ? (
      <div className="mt-1.5">{children}</div>
    ) : (
      <p className="mt-1 break-words text-base font-semibold">{value || "—"}</p>
    )}
  </div>
);

/**
 * One shared thread per request — the salesperson and the shop see the same
 * messages. Status changes land here as system lines so the history reads in
 * order without a separate audit log.
 */
export const ChatThread = ({ requestId, user, title = "Chat" }) => {
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  const load = () => {
    if (!requestId) return;
    fetchServiceRequestMessages(requestId)
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => setMessages([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    setError(null);
    try {
      await postServiceRequestMessage(requestId, text, {
        name: user?.name,
        email: user?.email,
        userID: user?.userID,
      });
      setDraft("");
      load();
    } catch (err) {
      setError(err.message || "Message didn't send — try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="app-card flex h-full flex-col rounded-2xl border">
      <div className="flex items-center gap-2 border-b app-divider px-5 py-3.5">
        <ChatBubbleOutlineIcon fontSize="small" className="app-muted" />
        <h2 className="font-semibold">{title}</h2>
      </div>

      <div className="min-h-[18rem] flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {messages === null && <LoadingState label="Loading messages…" />}
        {messages?.length === 0 && (
          <EmptyState
            compact
            icon={ChatBubbleOutlineIcon}
            description="No messages yet. Anything you post here is visible to both the salesperson and the shop."
          />
        )}
        {(messages || []).map((message, index) =>
          message.system ? (
            <div
              key={index}
              className="app-surface rounded-xl border px-3 py-2 text-xs"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="app-muted font-semibold">
                  {message.from || "System"}
                </span>
                <span className="app-muted whitespace-nowrap">
                  {formatDate(message.date)}
                </span>
              </div>
              <p className="mt-0.5 break-words font-medium">
                {message.message}
              </p>
            </div>
          ) : (
            <div
              key={index}
              className="rounded-xl border border-sky-500/20 bg-sky-500/10 px-3.5 py-2.5"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">{message.from}</span>
                <span className="app-muted whitespace-nowrap text-xs">
                  {formatDate(message.date)}
                </span>
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm">
                {message.message}
              </p>
            </div>
          )
        )}
        <div ref={endRef} />
      </div>

      <div className="space-y-2 border-t app-divider px-5 py-3">
        {error && (
          <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}
        <div className="flex items-end gap-2">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Type here..."
            inputProps={{ "aria-label": "Message" }}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={send}
            disabled={sending || !draft.trim()}
            startIcon={
              sending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Dated progress timeline for the sales side: which steps are done and WHEN
 * each happened. Done-ness comes from the checklist (the source of truth);
 * dates come from the status-change log in the chat thread. Requests
 * imported from the old system have no change log, so their finished steps
 * show a check without a date rather than a made-up one.
 */
const TIMELINE_STEPS = [
  { key: "submitted", label: "Submitted" },
  {
    key: "homeowner_contacted",
    label: "Homeowner contacted",
    status: STATUS.CONTACTED,
  },
  { key: "job_quoted", label: "Job quoted", status: STATUS.QUOTED },
  { key: "parts_ordered", label: "Parts ordered", status: STATUS.PARTS },
  {
    key: "tech_service_completed",
    label: "Tech service completed",
    status: STATUS.BILLING,
  },
  {
    key: "office_service_completed",
    label: "Service completed",
    status: STATUS.COMPLETED,
  },
];

export const ProgressTimeline = ({ request }) => {
  const [statusDates, setStatusDates] = useState({});

  useEffect(() => {
    if (!request?._id) return;
    // Earliest time each status was reached, from the system log lines.
    fetchServiceRequestMessages(request._id)
      .then((messages) => {
        const dates = {};
        (Array.isArray(messages) ? messages : []).forEach((message) => {
          const to = message?.status_change?.to;
          if (!to || !message.date) return;
          if (!dates[to] || new Date(message.date) < new Date(dates[to])) {
            dates[to] = message.date;
          }
        });
        setStatusDates(dates);
      })
      .catch(() => setStatusDates({}));
  }, [request?._id]);

  const checklist = request?.checklist || {};
  const isDone = (step) =>
    step.key === "submitted" ? true : Boolean(checklist[step.key]);
  const dateOf = (step) =>
    step.key === "submitted" ? request?.entry_date : statusDates[step.status];

  // The first not-done step is "up next"; everything after is pending.
  const nextIndex = TIMELINE_STEPS.findIndex((step) => !isDone(step));

  return (
    <ol className="relative">
      {TIMELINE_STEPS.map((step, index) => {
        const done = isDone(step);
        const isNext = index === nextIndex;
        const date = done ? dateOf(step) : null;
        const last = index === TIMELINE_STEPS.length - 1;
        return (
          <li key={step.key} className="relative flex gap-3 pb-5 last:pb-0">
            {/* connector */}
            {!last && (
              <span
                aria-hidden="true"
                className={`absolute left-[11px] top-6 h-full w-0.5 rounded-full ${
                  done ? "bg-emerald-500/60" : "bg-slate-400/25"
                }`}
              />
            )}
            <span
              className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                done
                  ? "bg-emerald-500 text-white"
                  : isNext
                  ? "border-2 border-sky-500 bg-white ring-4 ring-sky-500/15 dark:bg-slate-900"
                  : "bg-slate-200 dark:bg-slate-700"
              }`}
            >
              {done ? "✓" : ""}
            </span>
            <div className="min-w-0 pt-0.5">
              <p
                className={`text-sm ${
                  done
                    ? "font-medium"
                    : isNext
                    ? "font-semibold text-sky-700 dark:text-sky-300"
                    : "app-muted"
                }`}
              >
                {step.label}
                {isNext && (
                  <span className="ml-2 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-semibold text-sky-700 dark:text-sky-300">
                    up next
                  </span>
                )}
              </p>
              {date && (
                <p className="app-muted text-xs tabular-nums">
                  {formatDate(date)}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// Mirrors the server's multer limits.
const MAX_UPLOAD_FILES = 10;
const MAX_UPLOAD_BYTES = 64 * 1024 * 1024;

/**
 * Attachment list + "Add files" for a request. Add-only: uploads are part of
 * the request's audit trail, so there's no delete. Both detail pages render
 * this; `onUploaded` lets the page reload the request and its chat (the
 * upload logs a system line there).
 */
export const AttachmentsCard = ({ request, user, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const files = request?.files || [];

  const pick = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;
    setError(null);

    if (picked.length > MAX_UPLOAD_FILES) {
      setError(`You can add up to ${MAX_UPLOAD_FILES} files at a time.`);
      return;
    }
    const tooBig = picked.find((file) => file.size > MAX_UPLOAD_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is over the 64 MB limit.`);
      return;
    }

    setUploading(true);
    try {
      await uploadServiceRequestFiles(request._id, picked, {
        name: user?.name,
        email: user?.email,
        userID: user?.userID,
      });
      onUploaded?.();
    } catch (err) {
      setError(err.message || "Upload failed — try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="app-card rounded-2xl border">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b app-divider px-5 py-3.5">
        <h2 className="font-semibold">
          Attachments ({files.length})
        </h2>
        <Button
          size="small"
          variant="outlined"
          component="label"
          disabled={uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <AddPhotoAlternateIcon fontSize="small" />
            )
          }
        >
          {uploading ? "Uploading…" : "Add Files"}
          <input
            type="file"
            multiple
            hidden
            accept="image/*,application/pdf"
            onChange={pick}
          />
        </Button>
      </div>
      <div className="px-5 py-4">
        {error && (
          <p className="mb-3 rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}
        {files.length === 0 ? (
          <EmptyState
            compact
            icon={AddPhotoAlternateIcon}
            description="No attachments yet — photos of the issue save the technician a trip."
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {files.map((file) => (
              <li key={file.stored_name}>
                <a
                  href={serviceRequestFileUrl(request._id, file.stored_name)}
                  target="_blank"
                  rel="noreferrer"
                  className="app-surface flex min-h-[44px] items-center gap-2 rounded-xl border px-3 py-2 text-sm transition hover:bg-[color:var(--app-hover)]"
                >
                  <AttachFileIcon fontSize="small" className="app-muted shrink-0" />
                  <span className="min-w-0">
                    <span className="block truncate">
                      {file.original_name || file.stored_name}
                    </span>
                    {file.uploaded_by && (
                      <span className="app-muted block truncate text-xs">
                        {file.uploaded_by}
                      </span>
                    )}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
